"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAGE_PRUNE_NOTHING = exports.pagePruneChanged = exports.PAGE_ORPHAN_MARKS_MAP_KEY = void 0;
exports.purgePageSubtrees = purgePageSubtrees;
exports.readPageOrphanMarks = readPageOrphanMarks;
exports.pageLiveParentIds = pageLiveParentIds;
exports.pagePruneDeletedIds = pagePruneDeletedIds;
exports.pagePruneLeftGroupIds = pagePruneLeftGroupIds;
exports.prunePageDeleted = prunePageDeleted;
const page_document_maps_1 = require("./page-document-maps");
const page_structure_entry_1 = require("./page-structure-entry");
const page_structure_tree_1 = require("./page-structure-tree");
const page_structure_writer_1 = require("./page-structure-writer");
/**
 * Permanently removes blocks of a page, with every block and canvas element
 * below them: their structure entries and their fields, in part 0 or in the
 * document `parts` gives for their part. An ID without an entry loses only
 * its fields, in every given document.
 */
function purgePageSubtrees(doc, roots, parts = new Map()) {
    const all = new Map([[page_document_maps_1.PAGE_DOCUMENT_PART_ID, doc], ...parts]);
    const docs = {
        ...(0, page_structure_writer_1.pageDocumentStructure)(doc),
        loadedPartIds: () => [...all.keys()],
        partStructure: id => (all.has(id) ? (0, page_document_maps_1.pageStructureMap)(all.get(id)) : null),
        partBlocks: id => (all.has(id) ? (0, page_document_maps_1.pagePartBlocksMap)(all.get(id)) : null),
        partElements: id => all.has(id) ? (0, page_document_maps_1.pagePartElementsMap)(all.get(id)) : null,
    };
    const entries = (0, page_structure_writer_1.readPageDocsEntries)(docs);
    const tree = (0, page_structure_tree_1.derivePageTree)(entries);
    const ids = [...new Set(roots)];
    const seen = new Set(ids);
    // The iteration also visits the members pushed on the way.
    for (const id of ids) {
        for (const member of [
            ...(tree.children.get(id) ?? []),
            ...(tree.elements.get(id) ?? []),
        ]) {
            if (seen.has(member))
                continue;
            seen.add(member);
            ids.push(member);
        }
    }
    const docOf = (id, partId) => {
        if (partId === page_document_maps_1.PAGE_DOCUMENT_PART_ID)
            return doc;
        const part = parts.get(partId);
        if (!part)
            throw new page_structure_writer_1.PageStructureWriteError(`Block ${id} lies in part ${partId}, whose document is not given`);
        return part;
    };
    const fieldsIn = new Map();
    for (const id of ids) {
        const partId = entries.get(id)?.s;
        for (const target of partId === undefined
            ? [doc, ...parts.values()]
            : [docOf(id, partId)])
            fieldsIn.set(target, [...(fieldsIn.get(target) ?? []), id]);
    }
    const deleteFields = (target) => {
        const blocks = (0, page_document_maps_1.pagePartBlocksMap)(target);
        const elements = (0, page_document_maps_1.pagePartElementsMap)(target);
        for (const id of fieldsIn.get(target) ?? []) {
            (0, page_document_maps_1.pageStructureMap)(target).delete(id);
            blocks.delete(id);
            elements.delete(id);
        }
    };
    doc.transact(() => {
        deleteFields(doc);
    });
    for (const target of fieldsIn.keys())
        if (target !== doc)
            target.transact(() => deleteFields(target));
    return ids.length;
}
/**
 * Map of the page head that holds, by entry id, the mark of a live entry
 * without fields in its part, as the server prune saw it. Only that prune
 * writes and reads it: an entry is never rewritten for its mark, so a
 * concurrent client write to the entry is never lost.
 */
exports.PAGE_ORPHAN_MARKS_MAP_KEY = 'pageOrphanMarks';
const orphanMarksMap = (doc) => doc.getMap(exports.PAGE_ORPHAN_MARKS_MAP_KEY);
/** The marks of live entries without fields, by entry id. */
function readPageOrphanMarks(doc) {
    const marks = new Map();
    for (const [id, mark] of orphanMarksMap(doc))
        if (typeof mark === 'object' &&
            mark !== null &&
            Number.isFinite(mark.since) &&
            typeof mark.kept === 'boolean')
            marks.set(id, { since: mark.since, kept: mark.kept });
    return marks;
}
/** The ids live entries lie below. */
function pageLiveParentIds(entries) {
    const parents = new Set();
    for (const entry of entries.values())
        if (entry.p !== null && (0, page_structure_tree_1.isPageTreeEntry)(entry))
            parents.add(entry.p);
    return parents;
}
/** True when a prune changed the page. */
const pagePruneChanged = (counts) => counts.deleted +
    counts.orphanFields +
    counts.orphanEntries +
    counts.orphansMarked +
    counts.orphansCleared +
    counts.orphansKept >
    0;
exports.pagePruneChanged = pagePruneChanged;
/** The counts of a prune that changed nothing. */
exports.PAGE_PRUNE_NOTHING = {
    deleted: 0,
    orphanFields: 0,
    orphanEntries: 0,
    orphansMarked: 0,
    orphansCleared: 0,
    orphansKept: 0,
};
/**
 * Expired tombstones and their transitive descendants through the parent relation.
 * Read raw relationships: the visible tree already hides these descendants.
 */
function pagePruneDeletedIds(entries, expiredMs) {
    const children = new Map();
    const removed = new Set();
    for (const [id, entry] of entries) {
        if (entry.p === page_structure_tree_1.PAGE_TREE_DELETED_ID && Number(entry.d) <= expiredMs)
            removed.add(id);
        const parent = entry.p;
        if (parent && parent !== page_structure_tree_1.PAGE_TREE_DELETED_ID) {
            const siblings = children.get(parent);
            if (siblings)
                siblings.push(id);
            else
                children.set(parent, [id]);
        }
    }
    for (const id of removed)
        for (const child of children.get(id) ?? [])
            removed.add(child);
    return removed;
}
/** Live members retain their content when their group is pruned. */
function pagePruneLeftGroupIds(entries, removed) {
    const left = new Set();
    for (const [id, entry] of entries)
        if (entry.p !== page_structure_tree_1.PAGE_TREE_DELETED_ID &&
            !removed.has(id) &&
            entry.g !== undefined &&
            removed.has(entry.g))
            left.add(id);
    return left;
}
/**
 * Prunes the loaded parts of a page: expired deleted entries and fields
 * without a structure entry are removed. A live entry without fields in its part is marked
 * when first seen and goes once `windowMs` passed since, as long as a
 * device may still send its fields, unless live entries lie below it; the
 * mark goes when the fields return.
 */
function prunePageDeleted(doc, nowMs, windowMs, docs) {
    if (!Number.isFinite(nowMs) || !Number.isFinite(windowMs) || windowMs < 0)
        throw new RangeError('Invalid page deletion retention window');
    const entries = (0, page_structure_writer_1.readPageDocsEntries)(docs);
    const marks = orphanMarksMap(doc);
    const marked = readPageOrphanMarks(doc);
    const loaded = new Set(docs.loadedPartIds());
    const liveParents = pageLiveParentIds(entries);
    let deleted = 0;
    let orphanFields = 0;
    let orphanEntries = 0;
    let orphansMarked = 0;
    let orphansCleared = 0;
    let orphansKept = 0;
    // Nest the loaded documents' transactions so each emits one update.
    const partDocs = new Set();
    for (const partId of loaded) {
        const partDoc = docs.partBlocks(partId)?.doc ?? docs.partElements(partId)?.doc;
        if (partDoc && partDoc !== doc)
            partDocs.add(partDoc);
    }
    const transact = (remaining, fn) => {
        const next = remaining.pop();
        if (next)
            next.transact(() => transact(remaining, fn));
        else
            fn();
    };
    const expiredMs = nowMs - windowMs;
    const removed = pagePruneDeletedIds(entries, expiredMs);
    const leftGroups = pagePruneLeftGroupIds(entries, removed);
    doc.transact(() => transact([...partDocs], () => {
        for (const id of marked.keys()) {
            if (entries.get(id)?.p !== page_structure_tree_1.PAGE_TREE_DELETED_ID && entries.has(id))
                continue;
            marks.delete(id);
            orphansCleared++;
        }
        for (const [id, entry] of entries) {
            if (!loaded.has(entry.s))
                continue;
            const fields = (0, page_structure_entry_1.isPageElementKind)(entry.k)
                ? docs.partElements(entry.s)
                : docs.partBlocks(entry.s);
            if (!fields)
                continue;
            const mark = marked.get(id);
            if (leftGroups.has(id)) {
                const { g: _previous, ...rest } = entry;
                docs.partStructure(entry.s).set(id, (0, page_structure_entry_1.encodePageStructureEntry)(rest));
            }
            if (removed.has(id)) {
                docs.partStructure(entry.s).delete(id);
                fields.delete(id);
                if (marks.has(id)) {
                    marks.delete(id);
                    orphansCleared++;
                }
                deleted++;
            }
            else if (entry.p === page_structure_tree_1.PAGE_TREE_DELETED_ID) {
                continue;
            }
            else if (fields.has(id)) {
                if (mark === undefined)
                    continue;
                marks.delete(id);
                orphansCleared++;
            }
            else if (mark === undefined) {
                marks.set(id, { since: nowMs, kept: false });
                orphansMarked++;
            }
            else if (mark.since > expiredMs) {
                continue;
            }
            else if (liveParents.has(id)) {
                if (mark.kept)
                    continue;
                marks.set(id, { since: mark.since, kept: true });
                orphansKept++;
            }
            else {
                docs.partStructure(entry.s).delete(id);
                marks.delete(id);
                orphanEntries++;
            }
        }
        for (const partId of loaded) {
            for (const fields of [
                docs.partBlocks(partId),
                docs.partElements(partId),
            ]) {
                if (!fields)
                    continue;
                for (const id of fields.keys()) {
                    if (docs.partStructure(partId).has(id))
                        continue;
                    fields.delete(id);
                    orphanFields++;
                }
            }
        }
    }));
    return {
        deleted,
        orphanFields,
        orphanEntries,
        orphansMarked,
        orphansCleared,
        orphansKept,
    };
}
